import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Form, Input, InputNumber, Layout, message, Modal, Popconfirm,
  Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, LinkOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  fetchActions,
  createAction,
  updateAction,
  deleteAction,
  fetchActionUsage,
  type ActionUsage,
} from '../config/api';
import type { Action } from '../types/triage';

const { Content } = Layout;

interface ActionFormValues {
  action: string;
  instruction_link?: string;
  time_allocated_internal_mins?: number;
  time_allocated_external_mins?: number;
  equipment?: string[];
}

function formatMins(v: number | null): string {
  // Use em-dash so the empty-state glyph matches Equipment / Instructions
  // columns (consistent visual rhythm across the grid).
  if (v == null) return '—';
  if (v >= 60 && v % 60 === 0) return `${v / 60}h`;
  if (v >= 60) return `${Math.floor(v / 60)}h ${v % 60}m`;
  return `${v}m`;
}

const ActionsPage: React.FC = () => {
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Action | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<ActionFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  // UX-060: per-row pre-flight cache. Populated when the Popconfirm opens
  // for that action so operators see "X tickets + Y tasks would block" before
  // committing — instead of hitting a generic 409 after click.
  const [usageById, setUsageById] = useState<Record<number, ActionUsage | 'loading' | 'error'>>({});

  const ensureUsage = useCallback(async (id: number) => {
    if (usageById[id] && usageById[id] !== 'error') return;
    setUsageById((prev) => ({ ...prev, [id]: 'loading' }));
    try {
      const usage = await fetchActionUsage(id);
      setUsageById((prev) => ({ ...prev, [id]: usage }));
    } catch {
      setUsageById((prev) => ({ ...prev, [id]: 'error' }));
    }
  }, [usageById]);

  const loadActions = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchActions();
      setActions(rows);
    } catch (err: any) {
      messageApi.error(err.message || 'Failed to load actions');
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  const openCreateModal = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEditModal = (row: Action) => {
    setEditing(row);
    setModalOpen(true);
  };

  // Pre-fill (or clear) the form when the modal opens. Doing this here — after
  // the form has mounted — avoids the destroyOnClose + setFieldsValue race
  // where values set before mount get thrown away.
  useEffect(() => {
    if (!modalOpen) return;
    if (editing) {
      form.setFieldsValue({
        action: editing.action,
        instruction_link: editing.instruction_link ?? undefined,
        time_allocated_internal_mins: editing.time_allocated_internal_mins ?? undefined,
        time_allocated_external_mins: editing.time_allocated_external_mins ?? undefined,
        equipment: editing.equipment ?? [],
      });
    } else {
      form.resetFields();
    }
  }, [modalOpen, editing, form]);

  // Autocomplete suggestions: the union of every equipment value across loaded actions.
  const equipmentOptions = useMemo(() => {
    const set = new Set<string>();
    for (const a of actions) for (const e of a.equipment || []) set.add(e);
    return [...set].sort().map((e) => ({ value: e, label: e }));
  }, [actions]);

  const handleSave = async (values: ActionFormValues) => {
    setSaving(true);
    try {
      const payload = {
        action: values.action.trim(),
        instruction_link: values.instruction_link?.trim() || null,
        time_allocated_internal_mins: values.time_allocated_internal_mins ?? null,
        time_allocated_external_mins: values.time_allocated_external_mins ?? null,
        equipment: Array.isArray(values.equipment) ? values.equipment : [],
      };
      const saved = editing
        ? await updateAction(editing.id, payload)
        : await createAction(payload);
      setActions((prev) => {
        const without = prev.filter((a) => a.id !== saved.id);
        return [...without, saved].sort((a, b) => a.action.localeCompare(b.action));
      });
      messageApi.success(editing ? 'Action updated' : 'Action created');
      setModalOpen(false);
      form.resetFields();
    } catch (err: any) {
      messageApi.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: Action) => {
    try {
      await deleteAction(row.id);
      setActions((prev) => prev.filter((a) => a.id !== row.id));
      messageApi.success('Action deleted');
    } catch (err: any) {
      // 403 → friendly RBAC message (server returns {error:'forbidden',
      // detail:'requires one of: admin, fleet-ops'} after PR2). The bare
      // 'forbidden' string was confusing to operators who didn't realize
      // delete is an admin-only operation.
      if (err?.status === 403) {
        messageApi.error('Delete requires the admin or fleet-ops role.');
      } else {
        messageApi.error(err.message || 'Delete failed');
      }
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter((a) =>
      a.action.toLowerCase().includes(q) ||
      (a.equipment || []).some((e) => e.toLowerCase().includes(q))
    );
  }, [actions, search]);

  const columns: ColumnsType<Action> = [
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      sorter: (a, b) => a.action.localeCompare(b.action),
      defaultSortOrder: 'ascend',
      render: (v: string) => <Typography.Text strong style={{ fontSize: 13 }}>{v}</Typography.Text>,
    },
    {
      title: (
        <Tooltip title="Estimated time the SensorFlow team spends on this action (internal hours).">
          <span>Internal <InfoCircleOutlined style={{ fontSize: 10, color: '#bfbfbf', marginLeft: 2 }} /></span>
        </Tooltip>
      ),
      dataIndex: 'time_allocated_internal_mins',
      key: 'internal',
      width: 110,
      align: 'right',
      sorter: (a, b) => (a.time_allocated_internal_mins ?? 0) - (b.time_allocated_internal_mins ?? 0),
      render: (v: number | null) => <span style={{ fontSize: 12 }}>{formatMins(v)}</span>,
    },
    {
      title: (
        <Tooltip title="Estimated time the client / property side spends on this action (on-site coordination).">
          <span>External <InfoCircleOutlined style={{ fontSize: 10, color: '#bfbfbf', marginLeft: 2 }} /></span>
        </Tooltip>
      ),
      dataIndex: 'time_allocated_external_mins',
      key: 'external',
      width: 110,
      align: 'right',
      sorter: (a, b) => (a.time_allocated_external_mins ?? 0) - (b.time_allocated_external_mins ?? 0),
      render: (v: number | null) => <span style={{ fontSize: 12 }}>{formatMins(v)}</span>,
    },
    {
      title: 'Equipment',
      dataIndex: 'equipment',
      key: 'equipment',
      render: (equip: string[]) => (
        <Space size={4} wrap>
          {(equip || []).length === 0 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>—</Typography.Text>
          ) : (
            (equip || []).map((e) => (
              <Tag key={e} style={{ fontSize: 11, margin: 0 }}>{e}</Tag>
            ))
          )}
        </Space>
      ),
    },
    {
      title: 'Instructions',
      dataIndex: 'instruction_link',
      key: 'instruction_link',
      width: 140,
      render: (url: string | null) => url ? (
        <Typography.Link href={url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
          <LinkOutlined /> Open
        </Typography.Link>
      ) : (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>—</Typography.Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_v, row) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(row)}
            aria-label="Edit action"
          />
          {(() => {
            const usage = usageById[row.id];
            const loading = usage === 'loading';
            const errored = usage === 'error';
            const data = typeof usage === 'object' ? usage : null;
            const canDelete = data?.can_delete ?? true;
            return (
              <Popconfirm
                title="Delete this action?"
                description={
                  loading ? (
                    <span style={{ fontSize: 12 }}>Checking references…</span>
                  ) : errored ? (
                    <span style={{ fontSize: 12 }}>
                      Couldn't check references. Server will block if any exist.
                    </span>
                  ) : data ? (
                    canDelete ? (
                      <span style={{ fontSize: 12 }}>
                        No tickets, tasks, or open issues reference this — safe to remove.
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: '#cf1322' }}>
                        Referenced by{' '}
                        {data.tickets > 0 && <strong>{data.tickets} tickets</strong>}
                        {data.tickets > 0 && (data.tasks > 0 || data.issues > 0) && ' · '}
                        {data.tasks > 0 && <strong>{data.tasks} tasks</strong>}
                        {data.tasks > 0 && data.issues > 0 && ' · '}
                        {data.issues > 0 && <strong>{data.issues} open issues</strong>}
                        . Reassign or close them first.
                      </span>
                    )
                  ) : (
                    <span style={{ fontSize: 12 }}>Tasks and tickets referencing it will block this.</span>
                  )
                }
                okText="Delete"
                okButtonProps={{ danger: true, disabled: !canDelete && data != null }}
                onConfirm={() => handleDelete(row)}
                // Fetch usage when popover opens; cached afterwards.
                onOpenChange={(open) => { if (open) void ensureUsage(row.id); }}
              >
                <Button size="small" danger icon={<DeleteOutlined />} aria-label="Delete action" />
              </Popconfirm>
            );
          })()}
        </Space>
      ),
    },
  ];

  return (
    <Content style={{ padding: 16, overflow: 'auto' }}>
      {contextHolder}
      <Card
        size="small"
        title={
          <Space>
            <span>Actions</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {filtered.length} of {actions.length}
            </Typography.Text>
          </Space>
        }
        extra={
          <Space size={8}>
            <Input.Search
              allowClear
              placeholder="Search name or equipment"
              size="small"
              style={{ width: 240 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button size="small" icon={<ReloadOutlined />} onClick={loadActions} loading={loading}>
              Refresh
            </Button>
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              New action
            </Button>
          </Space>
        }
      >
        <Table<Action>
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={filtered}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: false }}
        />
      </Card>

      <Modal
        title={editing ? `Edit action: ${editing.action}` : 'New action'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="action"
            label="Name"
            rules={[{ required: true, message: 'Action name is required' }]}
          >
            <Input placeholder="e.g. Replace Node" autoFocus />
          </Form.Item>
          <Form.Item
            name="instruction_link"
            label="Instruction link"
            rules={[{ type: 'url', message: 'Must be a valid URL', warningOnly: true }]}
          >
            <Input placeholder="https://…" />
          </Form.Item>
          <Space.Compact block>
            <Form.Item
              name="time_allocated_internal_mins"
              label="Internal time (min)"
              style={{ flex: 1, marginRight: 8 }}
            >
              <InputNumber min={0} step={5} style={{ width: '100%' }} placeholder="e.g. 15" />
            </Form.Item>
            <Form.Item
              name="time_allocated_external_mins"
              label="External time (min)"
              style={{ flex: 1 }}
            >
              <InputNumber min={0} step={5} style={{ width: '100%' }} placeholder="e.g. 30" />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="equipment" label="Equipment" tooltip="Type and press Enter to add items, or pick from existing">
            <Select
              mode="tags"
              placeholder="Type equipment and press Enter"
              tokenSeparators={[',']}
              style={{ width: '100%' }}
              options={equipmentOptions}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editing ? 'Save' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Content>
  );
};

export default ActionsPage;
